import { api } from 'aws-blocks';
import BkButton from '../../Components/ui/BkButton';
import BkPanel from '../../Components/ui/BkPanel';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSnackBarContext } from '../../contexts/SnackBarContext';
import useApiAction from '../../hooks/use-api-action';
import { PLAYER_SKINS, skinSwatchBackground } from '../../player-colors';

export default function ProfileSkins() {
  const {
    currentUser,
    openLoginModal,
    userData: { ownedColorIds, equippedColorId, altSkinId } = {},
  } = useAuthContext();
  const { addSnackBar } = useSnackBarContext();

  const owned = [
    ...new Set([
      ...PLAYER_SKINS.filter((s) => s.isDefault).map((s) => s.id),
      ...(Array.isArray(ownedColorIds) ? ownedColorIds.map(String) : []),
    ]),
  ];

  const [equipSkin, loadingEquip] = useApiAction({
    action: async (payload: { skinId: string; slot: 'primary' | 'alt' }) =>
      api.equipSkin(payload.skinId, payload.slot),
    reloadUserData: true,
  });

  const [clearAlt, loadingClear] = useApiAction({
    action: async () => api.clearAltSkin(),
    reloadUserData: true,
  });

  async function handleEquip(skinId: string, slot: 'primary' | 'alt') {
    if (!currentUser) {
      openLoginModal();
      return;
    }
    if (!owned.includes(skinId)) {
      addSnackBar({
        title: 'Locked',
        message: 'Purchase this skin in the Store first.',
        variant: 'warning',
        timeout: 2500,
      });
      return;
    }
    try {
      await equipSkin({ skinId, slot });
      addSnackBar({
        title: 'Saved',
        message: slot === 'primary' ? 'Primary skin updated.' : 'Alt skin updated.',
        variant: 'success',
        timeout: 2000,
      });
    } catch (err) {
      addSnackBar({
        title: 'ERROR',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 2500,
      });
    }
  }

  async function handleClearAlt() {
    if (!currentUser) {
      openLoginModal();
      return;
    }
    await clearAlt();
    addSnackBar({
      title: 'Saved',
      message: 'Alt skin cleared.',
      variant: 'success',
      timeout: 2000,
    });
  }

  const loading = loadingEquip || loadingClear;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 px-4 py-6 sm:px-6 lg:px-10">
      <BkPanel className="space-y-4">
        <div>
          <h2 className="text-lg font-bold uppercase tracking-wider text-bk-gold">
            Skins
          </h2>
          <p className="mt-1 text-sm text-bk-muted">
            Primary and optional alt loadout. Online, if both players share a
            primary, one uses their alt.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {PLAYER_SKINS.map((skin) => {
            const isOwned = owned.includes(skin.id);
            const isPrimary = equippedColorId === skin.id;
            const isAlt = altSkinId === skin.id;
            return (
              <div
                key={skin.id}
                className={[
                  'border p-2 transition-colors',
                  isPrimary
                    ? 'border-bk-gold bg-bk-gold/10'
                    : isAlt
                      ? 'border-bk-accent bg-bk-accent/10'
                      : 'border-bk-border bg-bk-panel-raised',
                  !isOwned ? 'opacity-50' : '',
                ].join(' ')}
              >
                <div
                  className="mb-2 h-14 w-full border border-black/30"
                  style={{ background: skinSwatchBackground(skin) }}
                />
                <div className="mb-2 text-center text-xs font-semibold uppercase tracking-wide text-bk-text">
                  {skin.label}
                </div>
                <div className="flex flex-col gap-1">
                  <BkButton
                    variant="primary"
                    size="sm"
                    className="w-full !px-1 !text-[10px]"
                    disabled={!currentUser || loading || isPrimary || !isOwned}
                    onClick={() => void handleEquip(skin.id, 'primary')}
                  >
                    {isPrimary ? 'Primary' : 'Set Primary'}
                  </BkButton>
                  <BkButton
                    variant="secondary"
                    size="sm"
                    className="w-full !px-1 !text-[10px]"
                    disabled={
                      !currentUser || loading || isPrimary || isAlt || !isOwned
                    }
                    onClick={() => void handleEquip(skin.id, 'alt')}
                  >
                    {isAlt ? 'Alt' : 'Set Alt'}
                  </BkButton>
                </div>
              </div>
            );
          })}
        </div>
        {altSkinId ? (
          <div className="flex justify-end">
            <BkButton
              variant="ghost"
              size="sm"
              disabled={!currentUser || loading}
              onClick={() => void handleClearAlt()}
            >
              Clear Alt Skin
            </BkButton>
          </div>
        ) : null}
      </BkPanel>
    </div>
  );
}
